import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { Alert, AlertTitle } from "@material-ui/lab";
import { SettingsContext } from "../../context/Settings/SettingsContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId, target }) => {
  const classes = useStyles();
  const initialState = {
    name: "",
    greetingMessage: "",
    farewellMessage: "",
    isDefault: false,
    campaignsEnabled: false,
    wavoip: ""
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [nhubChannels, setNhubChannels] = useState([]);
  const [selectedNhubChannel, setSelectedNhubChannel] = useState("");
  const { settings } = useContext(SettingsContext);
  const [integrationNhub, setIntegrationNhub] = useState([]);

  useEffect(() => {
    const loadsettings = async () => {
      const integrationNhub = await settings.find(t => t.key === "integrationNhub");
      setIntegrationNhub({ value: integrationNhub?.value, text: integrationNhub?.text });
    }
    loadsettings();
  }, [settings]);

  useEffect(() => {
    const fetchNhubChannels = async () => {
      try {
        const { data } = await api.get("/nhubChannel/");
        setNhubChannels(data);
      } catch (err) {
        toastError(err);
      }
    }

    if (target === "channel") fetchNhubChannels();

  }, [open]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}`);
        setWhatsApp(data);

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = { ...values, queueIds: selectedQueueIds, type: target };

    try {
      if (target === "channel" && selectedNhubChannel) {
        const selectedNhubChannels = nhubChannels.find(
          channel => channel.id === selectedNhubChannel
        );

        if (selectedNhubChannels) {
          const channels = [selectedNhubChannels];
          await api.post("/nhubChannel/", {
            ...whatsappData,
            channels
          });

        }
      } else {
        if (whatsAppId) {
          await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        } else {
          await api.post("/whatsapp", whatsappData);
        }
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();

    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.name")}
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  {target !== "channel" &&
                    <>
                      <FormControlLabel
                        control={
                          <Field
                            as={Switch}
                            color="primary"
                            name="isDefault"
                            checked={values.isDefault}
                          />
                        }
                        label={i18n.t("whatsappModal.form.default")}
                      />

                      <FormControlLabel
                        control={
                          <Field
                            as={Switch}
                            color="primary"
                            name="campaignsEnabled"
                            checked={values.campaignsEnabled}

                          />
                        }
                        label={i18n.t("whatsappModal.form.campaignsEnabled")}
                      />
                    </>
                  }
                </div>

                {target !== "channel" &&
                  <>
                    <div>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.greetingMessage")}
                        type="greetingMessage"
                        multiline
                        rows={5}
                        fullWidth
                        name="greetingMessage"
                        error={
                          touched.greetingMessage && Boolean(errors.greetingMessage)
                        }
                        helperText={
                          touched.greetingMessage && errors.greetingMessage
                        }
                        variant="outlined"
                        margin="dense"
                      />
                    </div>
                    <div>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.farewellMessage")}
                        type="farewellMessage"
                        multiline
                        rows={5}
                        fullWidth
                        name="farewellMessage"
                        error={
                          touched.farewellMessage && Boolean(errors.farewellMessage)
                        }
                        helperText={
                          touched.farewellMessage && errors.farewellMessage
                        }
                        variant="outlined"
                        margin="dense"
                      />
                    </div>
                    <QueueSelect
                      selectedQueueIds={selectedQueueIds}
                      onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
                    />
                    <div>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.wavoipToken")}
                        type="text"
                        fullWidth
                        name="wavoip"
                        error={
                          touched.wavoip && Boolean(errors.wavoip)
                        }
                        helperText={
                          touched.wavoip && errors.wavoip
                        }
                        variant="outlined"
                        margin="dense"
                      />
                    </div>
                  </>
                }

                {target === "channel" &&
                  <>
                    <div>
                      {integrationNhub.value !== "enabled" ?
                        <DialogTitle id="form-dialog-title">
                          <Alert severity="info">
                            <AlertTitle>{i18n.t("connections.integrationNhubNotEnabled.title")}</AlertTitle>
                            {i18n.t("connections.integrationNhubNotEnabled.content")}
                          </Alert>
                        </DialogTitle>
                        :
                        <FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
                          <InputLabel>{i18n.t("Selecionar Canal")}</InputLabel>
                          <Field
                            as={Select}
                            required
                            name="channel"
                            color="primary"
                            label={i18n.t("Selecionar Canal")}
                            placeHolder={i18n.t("Selecionar Canal")}
                            style={{ minWidth: "200px" }}
                            onChange={e => {
                              const value = e.target.value;
                              setSelectedNhubChannel(value);
                            }}
                            displayEmpty
                          >
                            {nhubChannels.map(channel => (
                              <MenuItem key={channel.id} value={channel.id}>
                                {channel.name}
                              </MenuItem>
                            ))}

                          </Field>
                        </FormControl>
                      }
                    </div>
                  </>
                }
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={(target === "channel" && !selectedNhubChannel) ? true : isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
